import React, { useMemo, useRef, useState, useEffect } from 'react';
import { getImageStyle } from './imageUtils';
import ImageCascader from './ImageCascader';
import { useTranslation } from 'react-i18next';
import DuotoneLightIcon from '../common/DuotoneLightIcon';

// Common
import './../common/Utils.css';
import './Image.less';
import { useStore } from '../../store/store';
import Img from '../../images/placeholder-img.png';
import { Button, Progress, Segmented, Upload, message } from 'antd';
import {
  AimOutlined, ArrowDownOutlined, ArrowLeftOutlined, ArrowRightOutlined, ArrowUpOutlined,
} from '@ant-design/icons';
import { isAnUrl } from '../../utils/browser';
import { findWithRegex } from '../../utils/dataSync';
import { buildRecursiveFieldName, getSectionRecursiveMeta, shouldHideRecursivePlanningImageBox } from '../../utils/recursiveSections';
import { isAImgBox, isAVariableBox, reinitializeBoxWidthAndHeight } from '../../utils/boxes';
import { MASKS_TYPE, ItemTypes, PRIMARY_DARK, defaultImgAspectRatio, getPrimaryDark, viewerTypes } from '../../constants/constants';
import ButtonRefresh from '../common/ButtonRefresh';
import { leftShownStatus, useOverlay } from '../../contexts/OverlayContext';
import { usePreviewContext } from '../../contexts/PreviewContext';
import VariableSwitcher from '../common/VariableSwitcher';
import HideInFormSwitcher from '../common/HideInFormSwitcher';
import BoxFormControlsPortal from '../RightSide/BoxFormControlsPortal';
import MediaBankPicker from './MediaBankPicker';
import MediaResolutionSelect from './MediaResolutionSelect';
import { uploadDocumentMedia } from '../../requests/mediaUpload';
import {
  MEDIA_CONTEXTS, defaultResolutionFor, mediaSelection,
} from '../../shared/media/mediaResolution';
import { MAX_MEDIA_SIZE, MAX_MEDIA_SIZE_LABEL } from '../../shared/media/mediaLimits';
import {
  IMAGE_FIT, IMAGE_OFFSET_STEP, hasImageOffset, imageBoxFixedHeight, imageCoverTranslate, imageOffset,
  isFixedImageFit,
} from '../../shared/boxes/imageFit';
import { sectionAccentColor } from '../../shared/ui/editorAccents';
import { underlinedPanelHeadingStyle } from '../../shared/ui/panelHeading';
import { hideUploadProgress, showUploadProgress } from '../../utils/uploadProgress';
import { ContentState, Editor, EditorState } from 'draft-js';
import { decorator } from '../RichEditor/utils';
import BoxActionsPanel from '../common/BoxActionsPanel';
import { BackgroundColorPicker } from '../Layout/BackgroundColorPicker';
import { BorderManager } from '../Layout/BorderManager';
import LabelledRow from '../common/LabelledRow';
import { NavbarInputNumber } from '../Layout/NavbarInputNumber';
import Loader from '../../Loader';
import PlanningCaptureUnavailable from '../common/PlanningCaptureUnavailable';
// Sentinels du back pour une image planning récursive : capture en cours (loader)
// ou en échec. URL = image, "" = pas d'image (pas de loader). L'URL fraîche est
// swappée au broadcast WS (cf. store refreshRecursivePlanningImages).
import { FAILED_SCREENSHOT, PENDING_SCREENSHOT } from '../../utils/planningScreenshots';


const selector =
  (id) =>
    ({
      removeBox,
      updateBox,
      configuration,
      selectedBoxId,
      currentDocument,
      currentProposal,
    }) => ({
      currentDocument,
      removeBox,
      updateBox,
      configuration,
      isSelected: selectedBoxId === id,
      currentProposal,
    });

const Image = ({ box, updateImage, fromViewer }) => {
  const { currentDocument, currentProposal, isSelected } = useStore(selector(box.id));
  const header = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.HEADER.id))
  const footer = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id))
  const { updateLeftShownStatus } = useOverlay();
  const updateBox = useStore(({ updateBox }) => updateBox);
  const defaultVariablesApi = useStore(({ defaultVariablesApi }) => defaultVariablesApi)
  const recursiveModelFields = useStore(({ recursiveModelFields }) => recursiveModelFields);
  const currentTemplate = useStore(({ currentTemplate }) => currentTemplate);
  const sections = useStore(({ sections }) => sections);
  const fetchRecursiveSectionFields = useStore(({ fetchRecursiveSectionFields }) => fetchRecursiveSectionFields);
  const fromPdf = useStore(({ fromPdf }) => fromPdf);
  const { fromViewer: previewViewer } = usePreviewContext();
  const [retryCount, setRetryCount] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [imageRefreshKey, setImageRefreshKey] = useState(0);
  const [forcedImageSrc, setForcedImageSrc] = useState(null);
  // Ratio de l'image réellement chargée : « remplir » en a besoin pour savoir
  // quel côté caler sur la box, et `content.ratio` manque pour une image
  // variable.
  const [loadedRatio, setLoadedRatio] = useState(null);
  const ref = useRef();
  const dragCounter = useRef(0);
  const { t } = useTranslation();

  const recursiveModelName = useMemo(() => {
    const tplMeta = getSectionRecursiveMeta(currentTemplate);
    if (tplMeta.isRecursive && tplMeta.recursiveModelName) return tplMeta.recursiveModelName;
    if (box?.fromSectionId) {
      const section = sections?.find((s) => String(s.id) === String(box.fromSectionId));
      const meta = getSectionRecursiveMeta(section);
      if (meta.isRecursive) return meta.recursiveModelName;
    }
    return null;
  }, [currentTemplate, sections, box?.fromSectionId]);

  useEffect(() => {
    if (recursiveModelName && !recursiveModelFields?.[recursiveModelName]) {
      fetchRecursiveSectionFields(recursiveModelName);
    }
  }, [recursiveModelName, recursiveModelFields, fetchRecursiveSectionFields]);

  const defaultVariableApiSrc = useMemo(() => {
    if (!box.content.adminValue || !defaultVariablesApi) return null

    const keys = box.content.adminValue.split('.')
    if (keys.length === 3) {
      const indexKey = keys[0]
      const originKey = Object.keys(defaultVariablesApi.opportunities_variables[indexKey])[0]
      const categoryKey = keys[1]
      const informationKey = keys[2]
      return defaultVariablesApi.opportunities_variables[indexKey][originKey][categoryKey]?.[informationKey]
    }

    const categoryKey = keys[0]
    const informationKey = keys[1]

    if (defaultVariablesApi.offer_variables) {
      return defaultVariablesApi.offer_variables[categoryKey]?.[informationKey]
    }

    return defaultVariablesApi[categoryKey]?.[informationKey]
  }, [box.content.adminValue, defaultVariablesApi])

  const getNewImageBoxFromRefresh = (oldBox, freshDefaultVariablesApi = null) => {
    let newBox = { ...oldBox }

    // Utiliser les nouvelles données si disponibles, sinon les données actuelles
    const variablesApi = freshDefaultVariablesApi || defaultVariablesApi

    if (variablesApi && oldBox.content.adminValue) {
      const keys = oldBox.content.adminValue.split('.')
      let newSrc = null

      if (keys.length === 3) {
        const indexKey = keys[0]
        const originKey = Object.keys(variablesApi.opportunities_variables[indexKey])[0]
        const categoryKey = keys[1]
        const informationKey = keys[2]
        newSrc = variablesApi.opportunities_variables[indexKey][originKey][categoryKey]?.[informationKey]
      } else {
        const categoryKey = keys[0]
        const informationKey = keys[1]

        if (variablesApi.offer_variables) {
          newSrc = variablesApi.offer_variables[categoryKey]?.[informationKey]
        } else {
          newSrc = variablesApi[categoryKey]?.[informationKey]
        }
      }

      if (newSrc) {
        newBox.content = { ...newBox.content, src: newSrc }
      } else {
        const lastKey = keys[keys.length - 1]
        const beforeLastKey = keys[keys.length - 2]
        newBox.content = { ...newBox.content, src: `#{${beforeLastKey}.${lastKey}}` }
      }
    } else {
      const keys = oldBox.content.adminValue.split(".")
      const lastKey = keys[keys.length - 1]
      const beforeLastKey = keys[keys.length - 2]
      newBox.content = { ...newBox.content, src: `#{${beforeLastKey}.${lastKey}}` }
    }

    newBox.content = { ...newBox.content, ratio: null }
    // Contenir / remplir : la box garde sa taille, seule l'image change.
    if (!isFixedImageFit(newBox)) newBox.height = 'auto';
    return newBox
  }

  const handleOnClickRefresh = async (e) => {
    e.stopPropagation()

    // Image planning récursive (work_position.planning_*) : flux dédié (loader PENDING +
    // re-fetch force qui relance le microservice + swap au broadcast WS). Le flux
    // defaultVariablesApi ci-dessous ignore les champs récursifs -> placeholder #{...}.
    const handled = await useStore.getState().refreshRecursivePlanningImage(box.id)
    if (handled) {
      setForcedImageSrc(null) // laisser box.content.src (PENDING) piloter le loader
      return
    }

    // Récupérer les nouvelles données des variables depuis le serveur
    const fetchDefaultVariablesApi = useStore.getState().fetchDefaultVariablesApi
    await fetchDefaultVariablesApi()

    // Attendre un peu pour laisser le temps aux autres processus de se terminer
    await new Promise(resolve => setTimeout(resolve, 100))

    // Récupérer les nouvelles données du store
    const freshDefaultVariablesApi = useStore.getState().defaultVariablesApi

    // Forcer le rechargement de l'image
    setImageRefreshKey(prev => prev + 1)

    const newBox = getNewImageBoxFromRefresh(box, freshDefaultVariablesApi)

    // Forcer l'utilisation de la nouvelle URL
    setForcedImageSrc(newBox.content.src)

    if (updateImage) {
      updateImage(newBox)
    }
    else {
      // Utiliser directement l'action du store pour forcer la mise à jour
      const setStore = useStore.getState().setStore
      if (setStore) {
        const currentState = useStore.getState()
        const updatedBoxes = currentState.boxes.map(b =>
          b.id === box.id ? newBox : b
        )
        setStore({ boxes: updatedBoxes })
      } else {
        updateBox(box.id, (box) => {
          box = newBox
        });
      }
    }

    // Attendre encore un peu et forcer une nouvelle mise à jour pour s'assurer que ça prend
    setTimeout(() => {
      // Récupérer la box actuelle du store pour avoir la dernière version
      const currentBox = useStore.getState().boxes.find(b => b.id === box.id)
      if (currentBox) {
        const finalBox = getNewImageBoxFromRefresh(currentBox, freshDefaultVariablesApi)

        // Forcer à nouveau l'utilisation de la nouvelle URL
        setForcedImageSrc(finalBox.content.src)

        if (updateImage) {
          updateImage(finalBox)
        }
        else {
          // Utiliser directement l'action du store pour forcer la mise à jour
          const setStore = useStore.getState().setStore
          const currentState = useStore.getState()
          const updatedBoxes = currentState.boxes.map(b =>
            b.id === box.id ? finalBox : b
          )
          setStore({ boxes: updatedBoxes })
        }
      }
    }, 200)

    // Mécanisme de retry pour s'assurer que la mise à jour persiste
    const retryUpdate = (attempt = 0) => {
      if (attempt >= 5) return // Arrêter après 5 tentatives

      setTimeout(() => {
        const currentBox = useStore.getState().boxes.find(b => b.id === box.id)
        if (currentBox && currentBox.content.src !== newBox.content.src) {
          const retryBox = getNewImageBoxFromRefresh(currentBox, freshDefaultVariablesApi)

          if (updateImage) {
            updateImage(retryBox)
          }
          else {
            // Utiliser directement l'action du store pour forcer la mise à jour
            const setStore = useStore.getState().setStore
            const currentState = useStore.getState()
            const updatedBoxes = currentState.boxes.map(b =>
              b.id === box.id ? retryBox : b
            )
            setStore({ boxes: updatedBoxes })
          }

          retryUpdate(attempt + 1)
        }
      }, 500 + (attempt * 200)) // Délai croissant
    }

    retryUpdate()
  }

  const src = forcedImageSrc || (isAnUrl(box.content.src) ?
    `${box.content.src}${box.content.src.includes('?') ? '&' : '?'}refresh=${imageRefreshKey}` :
    Img);

  // Loader uniquement si le back a marqué la capture comme "en cours" (sentinel).
  // Pas de timeout : un poste sans planning renvoie "" (pas PENDING) -> jamais de loader.
  const showPlanningLoader = !forcedImageSrc && box.content.src === PENDING_SCREENSHOT;
  const showPlanningFailure = !forcedImageSrc && box.content.src === FAILED_SCREENSHOT;

  // Image planning récursive sans data : masquée dans le PDF et son aperçu
  // seulement. L'éditeur garde le placeholder, l'utilisateur voit qu'aucune
  // donnée n'est arrivée et peut poser une image à la place.
  const isPdfRender =
    fromPdf ||
    previewViewer === viewerTypes.PREVIEW ||
    new URLSearchParams(window.location.search).has('generate_pdf');
  const hidePlanningImage =
    isPdfRender && !forcedImageSrc && shouldHideRecursivePlanningImageBox(box);

  const desc = useMemo(() => {
    const regex = new RegExp(/#{.*?}/, 'm');
    if (isAnUrl(box.content.src)) return null;
    if (findWithRegex(regex, box.content.src)) {
      const variableKey = box.content.adminValue || box.content.src?.slice(2, -1);
      const parts = variableKey?.split('.') ?? [];
      if (parts.length === 2) {
        const [modelName, fieldKey] = parts;
        const field = recursiveModelFields?.[modelName]?.find((f) => f.key === fieldKey);
        if (field) return buildRecursiveFieldName(modelName, field.label, t);
        return t(`${modelName}.${fieldKey}`);
      }
      if (parts.length >= 2) {
        return t(`${parts[parts.length - 2]}.${parts[parts.length - 1]}`);
      }
      return t(variableKey);
    }
    if (box.type === ItemTypes.IMG_VARIABLE) {
      return <Editor
        readOnly
        editorState={box.name} />
    }
    return null
  }, [box.content.src, box.content.adminValue, box.type, box.name, t, recursiveModelFields])

  const waitAndReload = (event) => {
    const originalSrc = event.target.src;
    if (retryCount < 3) {
      event.target.src = Img;
      setTimeout(function () {
        event.target.src = originalSrc;
        setRetryCount(retryCount + 1);
      }, 300);
    } else {
      event.target.src = Img;
    }
  }

  /* Le fichier part directement au stockage : au-delà de quelques mégaoctets
     le base64 ne tient plus. `onProgress` laisse l'appelant choisir où montrer
     l'avancement — une barre dans le panneau, un message sur le canvas. */
  const uploadFile = async ({ file, currentDocument, currentProposal, updateImage, updateBox, box, header, footer, onProgress }) => {
    if (file.size > MAX_MEDIA_SIZE) {
      message.error({
        content: `La taille du fichier ne doit pas dépasser ${MAX_MEDIA_SIZE_LABEL}`,
        key: 'file-too-large',
      });
      return;
    }

    const documentId = currentDocument.type === 'template'
      ? currentDocument.id
      : currentProposal.id;
    const report = onProgress || showUploadProgress;

    try {
      report(0);
      const { selection } = await uploadDocumentMedia({
        documentId,
        documentType: currentDocument.type,
        file,
        onProgress: report,
      });

      const imgEl = document.createElement('img');
      // Ratio connu ou non, la box doit se poser : une image qui ne charge pas
      // ici (cache froid, réseau) ne doit pas laisser l'utilisateur sans rien.
      const apply = () => {
        if (updateImage) {
          updateImage(getNewImageBoxFromLoad({ oldBox: box, res: selection, imgEl, header, footer }))
        }
        else {
          updateBox(box.id, (box, column, columnWidth) => {
            box = getNewImageBoxFromLoad({ oldBox: box, res: selection, imgEl, column, columnWidth, header, footer })
          });
        }
        // Nouvelle image rangée dans le dossier auto du document -> rafraîchir
        // le picker de la banque.
        useStore.getState().bumpMediaBank();
      };
      imgEl.onload = apply;
      imgEl.onerror = apply;
      imgEl.src = selection.url;
    } catch (error) {
      message.error({
        content: "Erreur lors de l'upload de l'image",
        key: 'upload-error',
      });
    } finally {
      if (onProgress) onProgress(null);
      else hideUploadProgress();
    }
  }

  const getNewImageBoxFromLoad = ({ oldBox, res, imgEl, column, columnWidth, header, footer }) => {
    const box = oldBox;
    box.content = {
      ...box.content,
      src: res.url,
      ratio: imgEl.width / imgEl.height,
      // Palier de résolution retenu, persisté avec la box : il n'est jamais
      // recalculé à l'affichage. `mediaId` permet de rouvrir le sélecteur.
      mediaId: res.mediaId ?? null,
      mediaResolution: res.resolution ?? null,
    };
    // Contenir / remplir : la box garde sa taille, seule l'image change.
    if (isFixedImageFit(box)) return box
    box.height = 'auto';
    if (column && !column.drawMode) {
      box.width = reinitializeBoxWidthAndHeight({
        header,
        footer,
        box,
        columnWidth,
      }).width
    }
    return box
  }

  useEffect(() => {
    const handlePaste = async (event) => {
      if (!isSelected) {
        return;
      }

      // Essayer d'abord l'API Clipboard moderne (navigator.clipboard.read)
      try {
        if (navigator.clipboard && navigator.clipboard.read) {
          const clipboardItems = await navigator.clipboard.read();

          for (const item of clipboardItems) {
            for (const type of item.types) {
              if (type.startsWith('image/')) {
                event.preventDefault();
                event.stopPropagation();

                const blob = await item.getType(type);
                const file = new File([blob], `pasted-image-${Date.now()}.png`, { type });

                uploadFile({
                  file, currentDocument, currentProposal, updateImage, updateBox, box, header, footer
                });
                return;
              }
            }
          }

          // Gestion spéciale pour les captures d'écran Mac
          // Parfois les captures d'écran sont stockées comme text/plain avec l'URL de l'image
          for (const item of clipboardItems) {
            for (const type of item.types) {
              if (type === 'text/plain') {
                try {
                  const text = await item.getType(type);
                  const textContent = await text.text();

                  // Vérifier si c'est une URL d'image temporaire Mac
                  if (textContent.includes('file://') && textContent.includes('.png')) {
                    event.preventDefault();
                    event.stopPropagation();

                    // Essayer de récupérer l'image depuis l'URL
                    try {
                      const response = await fetch(textContent);
                      const blob = await response.blob();
                      const file = new File([blob], `mac-screenshot-${Date.now()}.png`, { type: 'image/png' });

                      uploadFile({
                        file, currentDocument, currentProposal, updateImage, updateBox, box, header, footer
                      });
                      return;
                    } catch (fetchError) {
                      // Ignorer l'erreur et continuer
                    }
                  }
                } catch (textError) {
                  // Ignorer l'erreur et continuer
                }
              }
            }
          }
        }
      } catch (error) {
        // Ignorer l'erreur et continuer
      }

      // Fallback vers l'ancienne API (event.clipboardData)
      if (event.clipboardData && event.clipboardData.items) {
        const items = event.clipboardData.items;

        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            event.stopPropagation();

            const file = item.getAsFile();
            if (file) {
              uploadFile({
                file, currentDocument, currentProposal, updateImage, updateBox, box, header, footer
              });
            }
            break;
          }
        }
      }
    };

    // Ajouter l'écouteur d'événement sur le document pour capturer tous les événements de collage
    document.addEventListener('paste', handlePaste, true);

    return () => {
      document.removeEventListener('paste', handlePaste, true);
    };
  }, [box, currentDocument, currentDocument.id, currentDocument.type, currentProposal, currentProposal.id, footer, header, isSelected, updateBox, updateImage]);

  const handleDrop = (e) => {
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (isDraggingImage && file && file.type.startsWith('image/')) {
        e.preventDefault();
        e.stopPropagation();
        uploadFile({
          file, currentDocument, currentProposal, updateImage, updateBox, box, header, footer
        });
        setIsDraggingImage(false);
        return;
      }
    }

    const file = e.dataTransfer?.files?.[0];
    if (isDraggingImage && file && file.type.startsWith('image/')) {
      e.preventDefault();
      e.stopPropagation();
      uploadFile({
        file, currentDocument, currentProposal, updateImage, updateBox, box, header, footer
      })
      setIsDraggingImage(false);
    }
  };

  const handleDragEnter = (e) => {
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        setIsDraggingImage(true);
        return;
      }
    }

    const isImage = Array.from(e.dataTransfer?.items || []).some(item =>
      item.type.startsWith('image/')
    );

    if (isImage) {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;
      setIsDraggingImage(true);
    } else {
      setIsDraggingImage(false);
    }
  };

  const handleDragLeave = (e) => {
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
          setIsDraggingImage(false);
        }
        return;
      }
    }

    const isImage = Array.from(e.dataTransfer?.items || []).some(item =>
      item.type.startsWith('image/')
    );

    if (isImage) {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDraggingImage(false);
      }
    }
  }

  // Pas d'image planning (poste sans planning) -> rien à afficher.
  if (hidePlanningImage) return null;

  const imageSrc = src.includes('localhost') || src.includes('127.0.0.1') ? src : src.replace('http://', 'https://');
  const frameStyle = {
    ...getImageStyle(
      box.backgroundColor,
      box.border,
      currentDocument.type === 'template',
    ),
    width: box.width,
    height: box.height,
    // Toute l'image : le fond et la bordure s'estompent avec elle.
    opacity: box.opacity,
  };
  // Remplir : pas d'object-fit: cover, que Chrome réduit en crénelant l'image
  // dès que la réduction est forte. L'image est calée sur le côté qui couvre
  // la box, l'autre suit son ratio, et le cadre coupe le surplus. Hauteur pas
  // encore figée : rendu object-fit, faute de cadre à remplir.
  const coverFrame = box.imageFit === IMAGE_FIT.cover
    && typeof box.width === 'number'
    && typeof box.height === 'number';
  const imageRatio = loadedRatio || box.content.ratio || defaultImgAspectRatio;
  const coverImageSize = imageRatio > box.width / box.height
    ? { width: 'auto', height: '100%' }
    : { width: '100%', height: 'auto' };
  // Décalage choisi par l'utilisateur : l'image glisse dans le cadre, qui
  // coupe toujours le surplus (le décalage ne dépasse jamais ce surplus).
  const coverTranslate = hasImageOffset(box) ? imageCoverTranslate(box, imageRatio) : null;

  return (
    <div className={`image-box ${isDraggingImage ? 'is-dragging-image' : ""}`}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDoubleClick={(event) => {
        if (isAVariableBox(box?.type)) {
          updateLeftShownStatus(leftShownStatus["VARIABLES"], box.id)
        }
        event.stopPropagation()
      }}
    >
      {currentDocument.type !== 'template' &&
        box.content.adminValue &&
        box.content.src !== defaultVariableApiSrc &&
        !fromViewer && isHovered &&
        <div style={{
          position: 'absolute',
          zIndex: 9999,
          top: 0,
          right: 0
        }}>
          <ButtonRefresh handleOnClickRefresh={handleOnClickRefresh} />
        </div>
      }
      {showPlanningLoader ? (
        <div
          style={{
            width: box.width,
            height: box.height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader />
        </div>
      ) : showPlanningFailure ? (
        <div style={{ width: box.width, height: box.height }}>
          <PlanningCaptureUnavailable />
        </div>
      ) : coverFrame ? (
        // Fond, bordure et opacité passent au cadre, qui tient la place de la
        // balise. Flex centre l'image, surplus réparti des deux côtés.
        <div
          style={{
            ...frameStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <img
            key={`${box.id}-${imageRefreshKey}`}
            ref={ref}
            alt='rand img'
            onError={waitAndReload}
            onLoad={(event) => {
              const { naturalWidth, naturalHeight } = event.currentTarget;
              if (naturalWidth && naturalHeight) setLoadedRatio(naturalWidth / naturalHeight);
            }}
            src={imageSrc}
            style={{
              ...coverImageSize,
              maxWidth: 'none',
              flexShrink: 0,
              ...(coverTranslate ? { transform: `translate(${coverTranslate.x}px, ${coverTranslate.y}px)` } : {}),
            }}
          />
        </div>
      ) : (
        <img
          key={`${box.id}-${imageRefreshKey}`}
          ref={ref}
          alt='rand img'
          onError={waitAndReload}
          src={imageSrc}
          style={{
            ...frameStyle,
            // Contenir / remplir : l'image s'ajuste à la box, centrée.
            ...(isFixedImageFit(box) ? { objectFit: box.imageFit, objectPosition: 'center' } : {}),
          }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          top: '15%',
          left: '15%',
          fontWeight: 'bold',
          pointerEvents: 'none',
          wordBreak: 'break-word'
        }}
      >
        {desc}
      </div>
      {isSelected && (
        <>
          {/* Une ligne par réglage, libellé à gauche, comme le panneau des
              formes : trois pastilles posées côte à côte ne disaient pas
              laquelle réglait quoi. */}
          <BoxFormControlsPortal title={t('style')}>
            <LabelledRow label={t('opacity')}>
              {/* En % à l'écran, entre 0 et 1 dans la box. Pas de 0 : une
                  image invisible n'a pas d'usage, et le champ vidé revient à
                  100 %. */}
              <NavbarInputNumber
                width={80}
                min={10}
                max={100}
                step={10}
                suffix='%'
                value={Math.round((box.opacity ?? 1) * 100)}
                onChange={(value) => updateBox(box.id, (b) => {
                  b.opacity = value / 100;
                })}
                defaultValue={100}
              />
            </LabelledRow>
            <LabelledRow label={t('image-fit')} stacked>
              <Segmented
                block
                size='small'
                style={{ width: '100%' }}
                value={box.imageFit ?? IMAGE_FIT.auto}
                options={[IMAGE_FIT.auto, IMAGE_FIT.contain, IMAGE_FIT.cover].map((fit) => ({
                  value: fit,
                  label: t(`image-fit-${fit}`),
                  title: t(`image-fit-${fit}-hint`),
                }))}
                onChange={(fit) => updateBox(box.id, (b) => {
                  b.imageFit = fit;
                  // Auto : la hauteur suit de nouveau l'image. Contenir /
                  // remplir : on fige la hauteur affichée, la box ne bouge pas.
                  if (fit === IMAGE_FIT.auto) b.height = 'auto';
                  else if (typeof b.height !== 'number') {
                    // Ratio pas encore connu (image variable) : celui de
                    // l'image affichée, pas le ratio par défaut.
                    const img = ref.current;
                    const shownRatio = img?.naturalWidth && img?.naturalHeight
                      ? img.naturalWidth / img.naturalHeight
                      : defaultImgAspectRatio;
                    b.height = imageBoxFixedHeight(b, shownRatio);
                  }
                })}
              />
            </LabelledRow>
            {box.imageFit === IMAGE_FIT.cover && (
              <LabelledRow label={t('image-offset')} stacked>
                {[
                  ['x', -1, ArrowLeftOutlined, 'left'],
                  ['y', -1, ArrowUpOutlined, 'up'],
                  ['y', 1, ArrowDownOutlined, 'down'],
                  ['x', 1, ArrowRightOutlined, 'right'],
                ].map(([axis, direction, Icon, key]) => (
                  <Button
                    key={key}
                    size='small'
                    icon={<Icon />}
                    title={t(`image-offset-${key}`)}
                    onClick={() => updateBox(box.id, (b) => {
                      const current = imageOffset(b)[axis];
                      const next = Math.max(-100, Math.min(100, current + direction * IMAGE_OFFSET_STEP));
                      if (axis === 'x') b.imageOffsetX = next;
                      else b.imageOffsetY = next;
                    })}
                  />
                ))}
                <Button
                  size='small'
                  icon={<AimOutlined />}
                  title={t('image-offset-reset')}
                  disabled={!hasImageOffset(box)}
                  onClick={() => updateBox(box.id, (b) => {
                    b.imageOffsetX = 0;
                    b.imageOffsetY = 0;
                  })}
                />
              </LabelledRow>
            )}
            <LabelledRow label={t('background-color')}>
              <BackgroundColorPicker />
            </LabelledRow>
            <LabelledRow label={t('border')}>
              <BorderManager part='border' />
            </LabelledRow>
            <LabelledRow label={t("border's-color")}>
              <BorderManager part='color' />
            </LabelledRow>
          </BoxFormControlsPortal>
          {/* Hors de la section ci-dessus : ImageComponent porte ses propres
              sections d'inspecteur (Affichage, Image, Banque d'images). */}
          <ImageComponent box={box} updateImage={updateImage} />
          <BoxActionsPanel />
        </>
      )}
    </div>
  );
};

// Sections de contrôles image. En panneau (inspecteur droite) : portées dans
// #RightSideQuickEditPortal via BoxFormControlsPortal. En `inline` (barre
// flottante FloatyImageMenu, pour une image posée dans un éditeur de texte type
// « Remarques ») : rendues sur place — ce portail n'existe pas dans ce contexte,
// donc BoxFormControlsPortal ne rendrait rien et la barre resterait vide.
const ImageControlsSection = ({ inline, title, order, layout, children }) => {
  if (!inline) {
    return (
      <BoxFormControlsPortal title={title} order={order} layout={layout}>
        {children}
      </BoxFormControlsPortal>
    );
  }
  return (
    <div style={{ order }}>
      {title && (
        // Même titre que dans l'inspecteur : la barre montre les mêmes sections.
        <div style={{
          fontSize: 12,
          ...underlinedPanelHeadingStyle(sectionAccentColor(title)),
          marginBottom: 8,
        }}>{title}</div>
      )}
      <div style={{
        display: 'flex',
        flexDirection: layout === 'row' ? 'row' : 'column',
        flexWrap: layout === 'row' ? 'wrap' : 'nowrap',
        alignItems: layout === 'row' ? 'center' : 'stretch',
        gap: layout === 'row' ? '10px 8px' : 12,
      }}>{children}</div>
    </div>
  );
};

export const ImageComponent = ({ box, updateImage, inline = false }) => {
  const { updateBox, currentDocument, currentProposal } = useStore(
    selector(box?.id)
  );
  const header = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.HEADER.id))
  const footer = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id))
  const { t } = useTranslation();

  const getNewImageBoxFromLoad = ({ oldBox, res, imgEl, column, columnWidth, header, footer }) => {
    const box = oldBox;
    box.content = {
      ...box.content,
      src: res.url,
      ratio: imgEl.width / imgEl.height,
      // Palier de résolution retenu, persisté avec la box : il n'est jamais
      // recalculé à l'affichage. `mediaId` permet de rouvrir le sélecteur.
      mediaId: res.mediaId ?? null,
      mediaResolution: res.resolution ?? null,
    };
    // Contenir / remplir : la box garde sa taille, seule l'image change.
    if (isFixedImageFit(box)) return box
    box.height = 'auto';
    if (column && !column.drawMode) {
      box.width = reinitializeBoxWidthAndHeight({
        header,
        footer,
        box,
        columnWidth,
      }).width
    }
    return box
  }

  const getNewImageBoxFromAdmin = (oldBox, adminValue, srcValue) => {
    const box = oldBox;
    box.content = { ...box.content, src: srcValue, ratio: null };
    if (!isFixedImageFit(box)) box.height = 'auto';
    box.content.adminValue = adminValue
    return box
  }

  /* Le fichier part directement au stockage : au-delà de quelques mégaoctets
     le base64 ne tient plus. `onProgress` laisse l'appelant choisir où montrer
     l'avancement — une barre dans le panneau, un message sur le canvas. */
  const uploadFile = async ({ file, currentDocument, currentProposal, updateImage, updateBox, box, header, footer, onProgress }) => {
    if (file.size > MAX_MEDIA_SIZE) {
      message.error({
        content: `La taille du fichier ne doit pas dépasser ${MAX_MEDIA_SIZE_LABEL}`,
        key: 'file-too-large',
      });
      return;
    }

    const documentId = currentDocument.type === 'template'
      ? currentDocument.id
      : currentProposal.id;
    const report = onProgress || showUploadProgress;

    try {
      report(0);
      const { selection } = await uploadDocumentMedia({
        documentId,
        documentType: currentDocument.type,
        file,
        onProgress: report,
      });

      const imgEl = document.createElement('img');
      // Ratio connu ou non, la box doit se poser : une image qui ne charge pas
      // ici (cache froid, réseau) ne doit pas laisser l'utilisateur sans rien.
      const apply = () => {
        if (updateImage) {
          updateImage(getNewImageBoxFromLoad({ oldBox: box, res: selection, imgEl, header, footer }))
        }
        else {
          updateBox(box.id, (box, column, columnWidth) => {
            box = getNewImageBoxFromLoad({ oldBox: box, res: selection, imgEl, column, columnWidth, header, footer })
          });
        }
        // Nouvelle image rangée dans le dossier auto du document -> rafraîchir
        // le picker de la banque.
        useStore.getState().bumpMediaBank();
      };
      imgEl.onload = apply;
      imgEl.onerror = apply;
      imgEl.src = selection.url;
    } catch (error) {
      message.error({
        content: "Erreur lors de l'upload de l'image",
        key: 'upload-error',
      });
    } finally {
      if (onProgress) onProgress(null);
      else hideUploadProgress();
    }
  }

  const [uploadPercent, setUploadPercent] = useState(null);

  const handleUploadImg = async (fileInfos) => {
    uploadFile({
      file: fileInfos.file, currentDocument, currentProposal, updateImage, updateBox, box, header, footer,
      onProgress: setUploadPercent,
    })
  };

  const accentColor = getPrimaryDark(currentDocument.type === 'template');

  // Applique un média de la banque : l'URL est déjà servie (pas d'upload), on
  // charge juste l'image pour connaître son ratio (comme un upload), puis on
  // réutilise getNewImageBoxFromLoad. Si le chargement échoue, on pose la src
  // sans ratio (hauteur auto) plutôt que de bloquer.
  //
  // Le palier retenu est `pdf` : c'est le contexte du proposal classique, et
  // poser l'original ferait entrer plusieurs mégaoctets dans un document qu'on
  // imprime à 1600 px.
  const applyBankMedia = (media, resolution) => {
    const selection = mediaSelection(
      media,
      resolution || defaultResolutionFor(media, MEDIA_CONTEXTS.pdf),
    );
    const imgEl = document.createElement('img');
    const apply = () => {
      if (updateImage) {
        updateImage(getNewImageBoxFromLoad({ oldBox: box, res: selection, imgEl, header, footer }));
      } else {
        updateBox(box.id, (b, column, columnWidth) => {
          b = getNewImageBoxFromLoad({ oldBox: b, res: selection, imgEl, column, columnWidth, header, footer });
        });
      }
    };
    imgEl.onload = apply;
    imgEl.onerror = apply;
    imgEl.src = selection.url;
  };

  return (
    <>
      <ImageControlsSection inline={inline} title={t('display')}>
        <HideInFormSwitcher boxId={box.id} variant='panel' />
        <VariableSwitcher boxId={box.id} variant='panel' />
        <ImageCascader
          onLoadImage={(srcValue, adminValue) => {
            if (updateImage) {
              updateImage(getNewImageBoxFromAdmin(box, adminValue, srcValue))
            }
            else {
              updateBox(box.id, (box, column, columnWidth) => {
                box = getNewImageBoxFromAdmin(box, adminValue, srcValue)
              });
            }
          }}
          selectedBox={box}
        />
      </ImageControlsSection>
      {/* Zone glisser-déposer d'image : remplace l'ancien bouton upload de la
          barre d'icônes. order={1} -> au-dessus de la banque.
          data-outside-ignore + preventDefault au mousedown : cliquer/déposer ne
          déselectionne pas la box (cf. useOutsideAlerter). */}
      <ImageControlsSection inline={inline} title={t('image')} order={1}>
        <div
          data-outside-ignore="true"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <Upload.Dragger
            data-outside-ignore="true"
            accept="image/png, image/gif, image/jpeg"
            name='file'
            customRequest={(e) => handleUploadImg(e)}
            showUploadList={false}
            disabled={!box || !isAImgBox(box.type)}
            className='image-dropzone'
          >
            <p style={{ margin: 0 }}>
              <DuotoneLightIcon name='cloud-arrow-up' weight='solid' style={{ fontSize: 26, color: accentColor }} />
            </p>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: PRIMARY_DARK }}>
              {t('drop-image')}
            </p>
          </Upload.Dragger>
          {/* Sans elle, un envoi de plusieurs dizaines de mégaoctets paraît
              planté : la zone de dépôt ne bouge pas d'un pixel. */}
          {uploadPercent != null && (
            <Progress percent={uploadPercent} size="small" status="active" />
          )}
        </div>
      </ImageControlsSection>
      {/* Banque d'images (company_work) : dossiers repliables, clic = remplace
          l'image de la box. order={2} -> tout en bas, pour scroller les images
          sans pousser la zone de dépôt. */}
      {/* Résolution : le palier posé sur la box, modifiable après coup. Ne
          s'affiche que pour un média venu de la banque — une image importée
          avant ce chantier n'a pas de paliers à proposer. */}
      {box.content?.mediaId != null && (
        <ImageControlsSection inline={inline} title={t('resolution')} order={3}>
          <MediaResolutionSelect
            mediaId={box.content.mediaId}
            resolution={box.content.mediaResolution}
            onChange={(resolution, media) => applyBankMedia(media, resolution)}
          />
        </ImageControlsSection>
      )}
      <ImageControlsSection inline={inline} title={t('image-bank')} order={2}>
        <MediaBankPicker onSelectMedia={applyBankMedia} />
      </ImageControlsSection>
    </>
  );
};

export default React.memo(Image);
